import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Send, Loader2, Sparkles, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STARTERS = [
  "My son is 8 and loves robots. Suggest a curriculum path.",
  "Is homeschooling legal in India? How does NIOS work?",
  "How do I balance homeschooling with a full-time job?",
  "What's the difference between IB and Cambridge for a 12-year-old?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat/send", { session_id: sessionId, message: msg });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assistant failed to respond");
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I had trouble answering. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 h-[calc(100vh-80px)] flex flex-col">
      <div className="mb-6">
        <div className="overline mb-2">AI Parent Assistant</div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-brand-blue">Ask anything about your child's learning.</h1>
      </div>

      <div className="surface flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {messages.length === 0 && (
            <div className="text-center py-10">
              <div className="w-16 h-16 rounded-2xl bg-brand-green-soft text-brand-green mx-auto mb-4 flex items-center justify-center"><Sparkles size={28} /></div>
              <h3 className="text-xl font-semibold text-brand-blue">How can I help?</h3>
              <p className="mt-2 text-brand-muted">Try one of these starters:</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    data-testid={`assistant-starter-${STARTERS.indexOf(s)}`}
                    onClick={() => send(s)}
                    className="text-left p-4 rounded-2xl border border-brand-border hover:border-brand-blue hover:bg-brand-bg text-sm text-brand-blue transition-colors"
                  >
                    <MessageCircle size={14} className="text-brand-green mb-2" />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-5 py-3 whitespace-pre-wrap leading-relaxed ${
                m.role === "user"
                  ? "bg-brand-blue text-white rounded-br-sm"
                  : "bg-brand-bg text-brand-blue border border-brand-border rounded-bl-sm"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-brand-bg text-brand-muted border border-brand-border rounded-2xl rounded-bl-sm px-5 py-3 flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} /> Thinking...
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-brand-border p-4 flex gap-2">
          <input
            data-testid="assistant-input"
            className="field flex-1"
            placeholder="Ask about curricula, careers, tutors, legal, or anything else..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
          />
          <button data-testid="assistant-send-btn" type="submit" disabled={loading || !input.trim()} className="btn-primary disabled:opacity-50">
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
